// @ts-nocheck — disabled until filmoscope supports player tracker data
import { useEffect } from 'react';
import { useAnalytics, useFeatureFlags, useGtmService } from '@funtech-int/yango-shared';

import { FilmoscopeApi } from '@kinopoisk-int/filmoscope';

import {
    createAnalyticsEvents,
    type PlayerEventParams,
} from '@/scenarios/player/lib/createAnalyticsEvents';
import { featureFlagsScheme } from '@/scenarios/player/lib/featureFlags';

import { usePlayback } from './usePlayback';

interface PlayerAnalyticsParams {
    filmoscopeInstance: FilmoscopeApi | null;
    contentId: string;
    psid: string;
    manifestUrl: string | null;
    viewStartedGtmEvent?: string;
}

export function usePlayerAnalytics({
    filmoscopeInstance,
    contentId,
    psid,
    manifestUrl,
    viewStartedGtmEvent,
}: PlayerAnalyticsParams) {
    const playback = usePlayback();
    const { flags } = useFeatureFlags(featureFlagsScheme);
    const isDynamicPrerollEnabled = flags.get('isDynamicPrerollEnabled');

    const analytics = useAnalytics(createAnalyticsEvents);
    const gtmService = useGtmService();
    useEffect(() => {
        if (!filmoscopeInstance) return;

        // separate function for gtm and event name prop drilling
        // until filmoscope supports player tracker data
        // then rely on playback.contentTypename and move to unsubStart function
        const sendGtmEvent = () => {
            if (viewStartedGtmEvent) {
                gtmService?.send({ event: viewStartedGtmEvent });
            }
        };

        const getEventParams = (): PlayerEventParams => ({
            contentId,
            psid,
            manifestUrl: manifestUrl ?? '',
            contentTypename: playback.contentTypename,
            vsid: playback.vsid.value,
            isDynamicPrerollEnabled,
        });

        const unsubStart = filmoscopeInstance.listen('playbackstart', () => {
            analytics.contentStarted(getEventParams());
        });

        const unsubEnd = filmoscopeInstance.listen('ended', () => {
            if (filmoscopeInstance.ended) {
                analytics.contentFinished(getEventParams());
            }
        });

        return () => {
            unsubStart();
            unsubEnd();
            sendGtmEvent();
        };
    }, [
        filmoscopeInstance,
        analytics,
        contentId,
        psid,
        manifestUrl,
        playback.contentTypename,
        isDynamicPrerollEnabled,
        playback,
        gtmService,
        viewStartedGtmEvent,
    ]);
}
